/**
 * Content Masking Utility
 * Applies masking to SMS content for GUI display only.
 * Does NOT affect actual SMS sending or stored data.
 */

// Mask amounts like ₹1234, Rs.5000, INR 10000, $500, 1,234.56
const maskAmounts = (text) => {
  return text.replace(
    /(?:(?:Rs\.?|INR|₹|\$|USD)\s*[\d,]+(?:\.\d{1,2})?|[\d,]+(?:\.\d{1,2})?\s*(?:Rs\.?|INR|₹|\$|USD|rupees?|dollars?))/gi,
    (match) => match.replace(/\d/g, 'x')
  );
};

// Mask OTP patterns like "OTP is 123456", "code: 5678", "PIN: 1234"
const maskOTP = (text) => {
  return text.replace(
    /(?:OTP|otp|code|Code|CODE|pin|Pin|PIN|verification\s+code|one.time.password)\s*(?:is|:|-|=)?\s*(\d{4,8})/gi,
    (match, digits) => match.replace(digits, 'x'.repeat(digits.length))
  );
};

// Mask all digit sequences (4+ digits)
const maskDigits = (text) => {
  return text.replace(/\d{4,}/g, (match) => 'x'.repeat(match.length));
};

// Mask custom characters/words
const maskCustomChars = (text, customChars) => {
  if (!customChars) return text;
  const words = customChars.split(',').map(w => w.trim()).filter(Boolean);
  let result = text;
  for (const word of words) {
    if (word.length === 0) continue;
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');
    result = result.replace(regex, 'x'.repeat(word.length));
  }
  return result;
};

// Mask mobile numbers using pattern (X=show, x=mask)
// Pattern always applies to the last 10 digits; country code prefix is preserved as-is
const maskMobile = (text, pattern) => {
  if (!pattern) pattern = 'XXXxxXxxXX';
  // Match numbers with optional country code prefix (+91, 91) followed by 10 digits
  return text.replace(/(\+?91)?(\d{10})\b/g, (fullMatch, prefix, last10) => {
    const maskedDigits = last10.split('').map((ch, i) => {
      if (i < pattern.length && pattern[i].toLowerCase() === 'x') return 'x';
      return ch;
    }).join('');
    return (prefix || '') + maskedDigits;
  });
};

// Mask template variables (replace specific {#var#} values)
const maskTemplateVars = (text, maskedTemplates, templateId) => {
  if (!maskedTemplates || maskedTemplates.length === 0) return text;
  
  // Find matching template
  const tmpl = maskedTemplates.find(t => t.template_id === templateId);
  if (!tmpl || !tmpl.masked_variables || tmpl.masked_variables.length === 0) return text;
  
  let result = text;
  for (const varName of tmpl.masked_variables) {
    // Match {#varName#} pattern and mask the value
    const escaped = varName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');
    result = result.replace(regex, 'xxxxxx');
  }
  return result;
};

/**
 * Apply all enabled masking types to content
 * @param {string} content - The SMS content to mask
 * @param {object} maskingSettings - The masking settings object
 * @param {array} maskingTemplates - Array of template masking configs
 * @param {string} templateId - The template ID of this message (optional)
 * @param {string} mobileNumber - The mobile number to mask (optional, for mobile field)
 * @returns {string} Masked content
 */
export const applyContentMasking = (content, maskingSettings, maskingTemplates = [], templateId = '', mobileNumber = '') => {
  if (!content || !maskingSettings || !maskingSettings.masking_enabled) return content;
  
  let result = content;
  
  // Apply in order: template vars first (most specific), then OTP, amount, custom, digits, mobile
  if (maskingSettings.template_var_mask && templateId) {
    result = maskTemplateVars(result, maskingTemplates, templateId);
  }
  if (maskingSettings.otp_mask) {
    result = maskOTP(result);
  }
  if (maskingSettings.amount_mask) {
    result = maskAmounts(result);
  }
  if (maskingSettings.custom_char_mask && maskingSettings.custom_chars) {
    result = maskCustomChars(result, maskingSettings.custom_chars);
  }
  if (maskingSettings.digit_mask) {
    result = maskDigits(result);
  }
  if (maskingSettings.mobile_mask) {
    result = maskMobile(result, maskingSettings.mobile_mask_pattern);
  }
  
  return result;
};

/**
 * Apply mobile number masking to a standalone mobile number field
 */
export const applyMobileMasking = (mobile, maskingSettings) => {
  if (!mobile || !maskingSettings || !maskingSettings.masking_enabled || !maskingSettings.mobile_mask) return mobile;
  return maskMobile(mobile, maskingSettings.mobile_mask_pattern);
};

export default applyContentMasking;
